import React from "react";
import {
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Table,
    Badge,
    Button,
    Progress,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Pagination,
    PaginationItem,
    PaginationLink
} from "reactstrap";
import Swal from "sweetalert2";
import defaultAvatar from "assets/img/brand/avatar-2.jpg";
import { reverseReputationPenalty } from "../../api/ApiServices";
import { styles, getTierBadge, getUserImage, renderPaginationItems } from "./ReputationUtils";

const DashboardUsersTab = ({
    repStats,
    loading,
    users,
    setUsers,
    usersPage,
    usersTotalPages,
    usersTotal,
    usersLimit,
    search,
    setSearch,
    setUsersPage,
    filters,
    setFilters,
    permissions,
    fetchUsers,
    toggleBadgeModal,
    setSelectedUser,
    setDrawerOpen,
    setModerationModal,
    fetchModerationAudits,
    setAdjustData,
    setAdjustModal,
    setHistoryModal,
    setFlagReason,
    setFlagModal,
    loadHistory,
    isHistoryLoading,
    handleAction
}) => {
    return (
        <>
            <Row className="mb-4">
                <Col lg="4" className="mb-4 mb-lg-0">
                    <div style={styles.statCard}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h5 className="text-uppercase text-muted ls-1 mb-1">Analytics</h5>
                                <h3 className="mb-0 text-dark">Score Distribution</h3>
                            </div>
                            <div style={{ ...styles.statIconBox, background: "linear-gradient(135deg, #11cdef 0%, #1171ef 100%)" }}>
                                <i className="fas fa-chart-line" />
                            </div>
                        </div>
                        <div className="mt-4">
                            <div className="d-flex justify-content-between text-sm font-weight-bold mb-1">
                                <span className="text-success"><i className="fas fa-arrow-up mr-1" />{repStats?.distribution?.[0]?.label || "High (700+)"}</span>
                                <span>{repStats?.distribution?.[0]?.percentage || 0}%</span>
                            </div>
                            <Progress value={repStats?.distribution?.[0]?.percentage || 0} color="success" className="mb-3" style={{ height: "6px" }} />

                            <div className="d-flex justify-content-between text-sm font-weight-bold mb-1">
                                <span className="text-warning"><i className="fas fa-minus mr-1" />{repStats?.distribution?.[1]?.label || "Medium (300-699)"}</span>
                                <span>{repStats?.distribution?.[1]?.percentage || 0}%</span>
                            </div>
                            <Progress value={repStats?.distribution?.[1]?.percentage || 0} color="warning" className="mb-3" style={{ height: "6px" }} />

                            <div className="d-flex justify-content-between text-sm font-weight-bold mb-1">
                                <span className="text-danger"><i className="fas fa-arrow-down mr-1" />{repStats?.distribution?.[2]?.label || "Low (< 300)"}</span>
                                <span>{repStats?.distribution?.[2]?.percentage || 0}%</span>
                            </div>
                            <Progress value={repStats?.distribution?.[2]?.percentage || 0} color="danger" style={{ height: "6px" }} />
                        </div>
                    </div>
                </Col>

                <Col lg="4" className="mb-4 mb-lg-0">
                    <div style={styles.statCard}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h5 className="text-uppercase text-muted ls-1 mb-1">
                                    Community
                                </h5>
                                <h3 className="mb-0 text-dark">Tier Breakdown</h3>
                            </div>

                            <div
                                style={{
                                    ...styles.statIconBox,
                                    background:
                                        "linear-gradient(135deg, #8965e0 0%, #bc00dd 100%)"
                                }}
                            >
                                <i className="fas fa-layer-group" />
                            </div>
                        </div>

                        <div className="mt-4">
                            <Row className="text-center">
                                {(repStats?.userTierStats || []).map((tier, idx) => {
                                    const tierColors = {
                                        Newbie: "#adb5bd",
                                        Explorer: "#11cdef",
                                        Contributor: "#5e72e4",
                                        Mentor: "#fb6340",
                                        Elite: "#f5365c"
                                    };

                                    const tierColor =
                                        tierColors[tier.name] || "#8898aa";

                                    return (
                                        <Col xs="6" lg="4" key={idx} className="mb-4">
                                            <div
                                                style={{
                                                    background: "#fff",
                                                    borderRadius: "14px",
                                                    padding: "18px 12px",
                                                    border: "1px solid #f1f1f1",
                                                    boxShadow:
                                                        "0 2px 10px rgba(0,0,0,0.03)",
                                                    minHeight: "120px",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent: "center",
                                                    alignItems: "center"
                                                }}
                                            >
                                                <div
                                                    className="font-weight-900"
                                                    style={{
                                                        fontSize: "26px",
                                                        color: tierColor,
                                                        lineHeight: 1
                                                    }}
                                                >
                                                    {tier.count >= 1000
                                                        ? `${(tier.count / 1000).toFixed(1)}k`
                                                        : tier.count}
                                                </div>

                                                <div
                                                    className="text-uppercase font-weight-bold mt-2"
                                                    style={{
                                                        fontSize: "11px",
                                                        letterSpacing: "0.5px",
                                                        color: "#8898aa",
                                                        wordBreak: "break-word"
                                                    }}
                                                >
                                                    {tier.name}
                                                </div>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    </div>
                </Col>
                <Col lg="4">
                    <div style={styles.statCard}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h5 className="text-uppercase text-muted ls-1 mb-1">Engagement</h5>
                                <div className="d-flex align-items-center">
                                    <h3 className="mb-0 text-dark">Badge Stats</h3>
                                    {repStats?.allBadges && (
                                        <Badge color="warning" className="ml-2 bg-gradient-warning text-white border-0 shadow-sm" style={{ padding: "4px 8px", fontSize: "11px" }}>
                                            {repStats.allBadges.reduce((sum, b) => sum + (b.count || 0), 0)} Total
                                        </Badge>
                                    )}
                                </div>
                            </div>
                            <div style={{ ...styles.statIconBox, background: "linear-gradient(135deg, #fb6340 0%, #fbb140 100%)" }}>
                                <i className="fas fa-medal" />
                            </div>
                        </div>
                        <div className="mt-4">
                            <div className="mb-3">
                                <h6 className="text-uppercase text-muted font-weight-bold ls-1 mb-3" style={{ fontSize: '0.65rem' }}>
                                    🏆 Top Badges
                                </h6>
                                <ul className="list-unstyled p-0 m-0">
                                    {(repStats?.badgeStats || []).slice(0, 5).map((badge, idx) => {
                                        const formattedName = badge?.name
                                            ? badge.name.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())
                                            : "Unknown Badge";
                                        return (
                                            <li key={idx} className="d-flex align-items-center mb-2 px-3 py-2 bg-white rounded border border-lighter shadow-xs">
                                                <div className={`mr-3 ${idx === 0 ? "text-primary" : idx === 1 ? "text-success" : "text-warning"}`}>
                                                    <i className={`fas ${idx === 0 ? "fa-certificate" : idx === 1 ? "fa-award" : "fa-medal"} fa-sm`} />
                                                </div>
                                                <div className="flex-grow-1 font-weight-bold text-xs text-dark">{formattedName}</div>
                                                <div className="text-primary font-weight-900 text-xs">{badge?.count || 0}</div>
                                            </li>
                                        );
                                    })}
                                </ul>
                                {repStats?.badgeSummary?.total > 0 && (
                                    <div className="mt-2 text-center">
                                        <Button
                                            color="link"
                                            className="text-xs text-info p-0 font-weight-bold border-0 bg-transparent"
                                            onClick={toggleBadgeModal}
                                        >
                                            View all badges ({repStats.badgeSummary.total}) <i className="fas fa-arrow-right ml-1"></i>
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            <Card className="shadow" style={{ borderRadius: "16px", border: "none", overflow: "visible" }}>
                <CardHeader
                    className="border-0 bg-white"
                    style={{ borderRadius: "16px 16px 0 0", padding: "24px" }}
                >
                    <Col md="12" className="p-0">
                        <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: "12px" }}>
                            <div>
                                <h3 className="mb-0" style={{ fontSize: "20px", fontWeight: 700, color: "#172b4d" }}>
                                    <i className="ni ni-trophy text-pink mr-2" />
                                    User Reputation Tracker / Score Viewer
                                </h3>
                            </div>

                            <div className="d-flex align-items-center tracker-boxx" style={{ gap: "12px" }}>
                                <InputGroup>
                                    <Input
                                        placeholder="Search by name, email..."
                                        value={search}
                                        onChange={(e) => {
                                            setSearch(e.target.value);
                                            setUsersPage(1);
                                        }}
                                        style={{
                                            borderRadius: "8px 0 0 8px",
                                            fontSize: "13px"
                                        }}
                                    />
                                    <InputGroupAddon addonType="append">
                                        <InputGroupText
                                            style={{
                                                background: "#f6f9fc",
                                                borderRadius: "0 8px 8px 0",
                                                cursor: "pointer"
                                            }}
                                        >
                                            <i className="fas fa-search text-muted" />
                                        </InputGroupText>
                                    </InputGroupAddon>
                                </InputGroup>

                                <InputGroup >
                                    <Input
                                        type="select"
                                        value={filters.suspicious}
                                        onChange={(e) => {
                                            setFilters({ ...filters, suspicious: e.target.value });
                                            setUsersPage(1);
                                        }}
                                        style={{ borderRadius: "8px", fontSize: "13px" }}
                                    >
                                        <option value="">All Users</option>
                                        <option value="true">Suspicious Only</option>
                                        <option value="false">Clear Only</option>
                                    </Input>
                                </InputGroup>

                                <InputGroup >
                                    <Input
                                        type="select"
                                        value={filters.sort}
                                        onChange={(e) => {
                                            setFilters({ ...filters, sort: e.target.value });
                                            setUsersPage(1);
                                        }}
                                        style={{ borderRadius: "8px", fontSize: "13px" }}
                                    >
                                        <option value="">Sort by</option>
                                        <option value="score_desc">Score: High to Low</option>
                                        <option value="score_asc">Score: Low to High</option>
                                        <option value="name_asc">Name: A to Z</option>
                                        <option value="name_desc">Name: Z to A</option>
                                    </Input>
                                </InputGroup>
                            </div>
                        </div>
                    </Col>
                </CardHeader>

                <CardBody className="p-0">
                    <div className="table-responsive">
                        <Table className="align-items-center table-flush" hover>
                            <thead className="thead-light">
                                <tr>
                                    <th scope="col" style={{ padding: "16px 24px" }}>User</th>
                                    <th scope="col">Score</th>
                                    <th scope="col">Tier</th>
                                    <th scope="col" style={{ minWidth: '240px' }}>Achievements</th>
                                    <th scope="col">Top Signals</th>
                                    <th scope="col" className="text-center">Trust</th>
                                    <th scope="col" className="text-center">Suspicious?</th>
                                    <th scope="col" className="text-right" style={{ paddingRight: "24px" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="8" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                            <div className="mt-2 text-muted">Fetching live reputation data...</div>
                                        </td>
                                    </tr>
                                ) : !users || users.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="text-center py-5">
                                            <h4 className="text-muted">No users found.</h4>
                                        </td>
                                    </tr>
                                ) : (
                                    users.map((u) => {
                                        const userName = u?.user?.name || u?.name || "Unknown User";
                                        const userEmail = u?.user?.email || u?.email || "No email";
                                        const userImage = u?.user?.image || u?.image || null;
                                        const userScore = u?.score || 0;
                                        const userTier = u?.tier || "Standard";
                                        const baseSignals = u?.signals || [];
                                         const securityFlags = [...(u?.security?.suspiciousFlags || [])];
                                         if (u?.suspicious && u?.reputationPenaltyScore > 0) {
                                             securityFlags.push("Msg Penalty");
                                         }
                                         const userSignals = [...baseSignals, ...securityFlags];

                                        const userSuspicious = u?.suspicious || false;
                                        const userFlagReason = u?.flagReason || "";
                                        const userReputationColor = u?.reputationColor || "#525f7f";
                                        const userId = u?.id || Math.random().toString();

                                        const trustScore = u?.security?.trustScore || 40;
                                        const riskScore = u?.security?.riskScore || 0;
                                        const isUnderReview = u?.security?.isUnderReview || false;

                                        const trustWeightLabel = trustScore >= 100 ? "1.0x" : trustScore >= 80 ? "0.8x" : trustScore >= 40 ? "0.4x" : "0.0x";
                                        const trustTierColor = trustScore >= 100 ? "success" : trustScore >= 80 ? "info" : trustScore >= 40 ? "primary" : "warning";

                                        return (
                                            <tr key={userId} style={styles.tableRow}>
                                                <td style={{ padding: "16px 24px" }}>
                                                    <div className="d-flex align-items-center">
                                                        <div
                                                            style={{
                                                                width: "32px",
                                                                height: "32px",
                                                                borderRadius: "50%",
                                                                overflow: "hidden",
                                                                marginRight: "12px",
                                                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                                background: "#eee",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center"
                                                            }}
                                                        >
                                                            <img
                                                                src={
                                                                    userImage
                                                                        ? userImage.startsWith("http")
                                                                            ? userImage
                                                                            : `https://cradleaibucket.s3.us-east-1.amazonaws.com/users-profile-images/${userImage}`
                                                                        : defaultAvatar
                                                                }
                                                                alt={userName || "User"}
                                                                style={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    objectFit: "cover"
                                                                }}
                                                                onError={(e) => {
                                                                    console.log("USER IMAGE FAILED =>", userImage);
                                                                    e.currentTarget.onerror = null;
                                                                    e.currentTarget.src = defaultAvatar;
                                                                }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <span className="font-weight-bold d-block text-dark">{userName}</span>
                                                            <span className="text-xs text-muted">{userEmail}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="font-weight-bold" style={{ fontSize: "16px", color: userScore > 700 ? "#2dce89" : userScore > 300 ? "#fb6340" : "#f5365c" }}>
                                                        {userScore}
                                                    </span>
                                                </td>
                                                <td>{getTierBadge(userTier, userReputationColor)}</td>
                                                <td>
                                                    <div className="d-flex flex-wrap gap-2 align-items-center" style={{ maxWidth: '220px', padding: '4px 0' }}>
                                                        {u.badges?.filter(b => b.level !== 'locked' || b.penaltyProgress > 0 || (b.progress?.current > 0)).map((b, idx) => {
                                                            const badgeName = b.badgeId.replace('_', ' ').toUpperCase();
                                                            const badgeIcon = b.badgeId === 'mentor' ? '📖' : b.badgeId === 'connector' ? '🤝' : b.badgeId === 'cross_country' ? '🌍' : b.badgeId === 'multi_language' ? '🔤' : '🏅';
                                                            const isLocked = b.level === 'locked';
                                                            const levelColor = isLocked ? '#8898aa' : (b.level === 'gold' ? '#f1c40f' : b.level === 'silver' ? '#adb5bd' : '#cd7f32');
                                                            const isPenalized = b.penaltyProgress > 0;

                                                            return (
                                                                <Badge
                                                                    key={idx}
                                                                    style={{
                                                                        backgroundColor: isPenalized ? '#f5365c10' : `${levelColor}10`,
                                                                        color: isPenalized ? '#f5365c' : levelColor,
                                                                        border: `1px solid ${isPenalized ? '#f5365c30' : `${levelColor}30`}`,
                                                                        padding: '5px 10px',
                                                                        borderRadius: '8px',
                                                                        fontSize: '10px',
                                                                        fontWeight: '800',
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        marginRight: '2px',
                                                                        marginBottom: '2px',
                                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                                                                        opacity: isLocked && !isPenalized ? 0.7 : 1
                                                                    }}
                                                                    title={isPenalized ? `Penalized: -${b.penaltyProgress} points` : `${badgeName} ${b.level}`}
                                                                >
                                                                    <span className="mr-1" style={{ filter: isLocked && !isPenalized ? 'grayscale(1)' : 'none' }}>{badgeIcon}</span>
                                                                    {badgeName}
                                                                    {isPenalized && <i className="fas fa-exclamation-circle ml-1" style={{ fontSize: '9px' }} />}
                                                                </Badge>
                                                            );
                                                        })}
                                                        {(!u.badges || u.badges.length === 0) && <span className="text-muted text-xs font-italic">No active badges</span>}
                                                    </div>
                                                </td>
                                                <td>
                                                    {userSignals.map((sig, idx) => {
                                                        const isSecurityFlag = securityFlags.includes(sig);
                                                        return (
                                                            <span
                                                                key={idx}
                                                                style={{
                                                                    ...styles.signalBadge,
                                                                    backgroundColor: isSecurityFlag ? '#f5365c15' : '#eef1f8',
                                                                    color: isSecurityFlag ? '#f5365c' : '#32325d',
                                                                    borderColor: isSecurityFlag ? '#f5365c30' : '#dee2e6',
                                                                    fontWeight: isSecurityFlag ? 'bold' : '600'
                                                                }}
                                                            >
                                                                {isSecurityFlag && <i className="fas fa-exclamation-triangle mr-1" style={{ fontSize: '9px' }} />}
                                                                {sig.replace(/_/g, ' ')}
                                                            </span>
                                                        );
                                                    })}
                                                    {userSignals.length === 0 && <span className="text-muted text-xs">No signals</span>}
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex flex-column align-items-center">
                                                        <Badge color={trustTierColor} pill className="mb-1" style={{ fontSize: '10px' }}>
                                                            {trustScore} Score
                                                        </Badge>
                                                        <span className="text-xs font-weight-bold text-muted">{trustWeightLabel} Weight</span>
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    {userSuspicious ? (
                                                        <div className="d-flex flex-column align-items-center">
                                                            <Badge color="danger" pill className="shadow-sm border border-white" style={{ position: "relative" }}>
                                                                <span className="pulse-dot" style={{ position: "absolute", left: "-6px", top: "50%", transform: "translateY(-50%)", width: "8px", height: "8px", background: "#f5365c", borderRadius: "50%", boxShadow: "0 0 0 4px rgba(245, 54, 92, 0.3)" }}></span>
                                                                FLAGGED
                                                            </Badge>
                                                            {userFlagReason && (
                                                                <div className="text-xs text-danger mt-1 font-italic" style={{ maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={userFlagReason}>
                                                                    {userFlagReason}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted"><i className="fas fa-check text-success mr-1" />Clear</span>
                                                    )}
                                                </td>
                                                <td className="text-right" style={{ paddingRight: "24px", position: "relative" }}>
                                                    <UncontrolledDropdown>
                                                        <DropdownToggle
                                                            className="btn-icon-only text-light shadow-none"
                                                            href="#pablo"
                                                            role="button"
                                                            size="sm"
                                                            color=""
                                                            onClick={(e) => e.preventDefault()}
                                                            style={{ background: 'transparent', border: 'none' }}
                                                        >
                                                            <i className="fas fa-ellipsis-v" style={{ fontSize: '16px', color: '#8898aa' }} />
                                                        </DropdownToggle>
                                                        <DropdownMenu className="dropdown-menu-arrow" right>
                                                            <DropdownItem
                                                                onClick={(e) => handleAction("View Details", u, e)}
                                                            >
                                                                <i className="fas fa-eye text-primary mr-3" />
                                                                <span>View Details</span>
                                                            </DropdownItem>

                                                            {permissions.canAdjust && (
                                                                <DropdownItem
                                                                    onClick={() => {
                                                                        setSelectedUser(u);
                                                                        fetchModerationAudits(u.id || u._id);
                                                                        setModerationModal(true);
                                                                    }}
                                                                >
                                                                    <i className="fas fa-shield-alt text-warning mr-3" />
                                                                    <span>Reputation Moderation</span>
                                                                </DropdownItem>
                                                            )}

                                                            {permissions.canAdjust && (
                                                                <DropdownItem
                                                                    onClick={() => {
                                                                        setSelectedUser(u);
                                                                        setAdjustData({ score: userScore, reason: "" });
                                                                        setAdjustModal(true);
                                                                    }}
                                                                >
                                                                    <i className="fas fa-sliders-h text-info mr-3" />
                                                                    <span>Adjust Score</span>
                                                                </DropdownItem>
                                                            )}

                                                            {u.isPenaltyActive && (
                                                                <DropdownItem
                                                                    onClick={async () => {
                                                                        const { value: reason } = await Swal.fire({
                                                                            title: 'Reverse Penalty',
                                                                            input: 'textarea',
                                                                            inputLabel: 'Reason for reversal',
                                                                            inputPlaceholder: 'Explain why the penalty is being reversed...',
                                                                            showCancelButton: true
                                                                        });
                                                                        if (reason) {
                                                                            try {
                                                                                const res = await reverseReputationPenalty(u.id, reason);
                                                                                if (res.status) {
                                                                                    Swal.fire("Reversed!", "Reputation restored successfully.", "success");
                                                                                    const originalVal = u.originalScore || u.historicalScore || 40;
                                                                                    const updatedU = {
                                                                                        ...u,
                                                                                        score: originalVal,
                                                                                        reputationPenaltyScore: 0,
                                                                                        suspicious: false,
                                                                                        isPenaltyActive: false,
                                                                                        flagReason: ""
                                                                                    };
                                                                                    setUsers(users.map(user => user.id === u.id ? updatedU : user));
                                                                                    setSelectedUser(updatedU);
                                                                                    fetchUsers(usersPage);
                                                                                }
                                                                            } catch (err) {
                                                                                Swal.fire("Error", "Failed to reverse penalty", "error");
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    <i className="fas fa-undo text-warning mr-3" />
                                                                    <span>Reverse Penalty</span>
                                                                </DropdownItem>
                                                            )}

                                                            <DropdownItem
                                                                disabled={isHistoryLoading}
                                                                onClick={() => {
                                                                    setSelectedUser(u);
                                                                    loadHistory(u.id, 1, 1);
                                                                    setHistoryModal(true);
                                                                }}
                                                            >
                                                                <i className="fas fa-history text-default mr-3" />
                                                                <span>View History</span>
                                                            </DropdownItem>

                                                            {permissions.canFlag && (
                                                                 userSuspicious ? (
                                                                     <DropdownItem
                                                                         onClick={(e) => handleAction("Mark as not suspicious", u, e)}
                                                                     >
                                                                         <i className="fas fa-check text-success mr-3" />
                                                                         <span>Mark as not suspicious</span>
                                                                     </DropdownItem>
                                                                 ) : (
                                                                     <DropdownItem
                                                                         onClick={() => {
                                                                             setSelectedUser(u);
                                                                             setFlagReason("");
                                                                             setFlagModal(true);
                                                                         }}
                                                                     >
                                                                         <i className="fas fa-flag text-danger mr-3" />
                                                                         <span>Mark as suspicious</span>
                                                                     </DropdownItem>
                                                                 )
                                                             )}
                                                        </DropdownMenu>
                                                    </UncontrolledDropdown>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    </div>
                </CardBody>
                {usersTotalPages > 1 && (
                    <CardFooter className="py-4 border-0 bg-transparent d-flex justify-content-between align-items-center">
                        <span className="text-muted small">
                            Showing {((usersPage - 1) * usersLimit) + 1} to {Math.min(usersPage * usersLimit, usersTotal)} of {usersTotal}
                        </span>
                        <nav aria-label="...">
                            <Pagination
                                className="pagination justify-content-end mb-0"
                                listClassName="justify-content-end mb-0"
                            >
                                <PaginationItem disabled={usersPage <= 1}>
                                    <PaginationLink
                                        href="#pablo"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            fetchUsers(usersPage - 1);
                                        }}
                                        tabIndex="-1"
                                    >
                                        <i className="fas fa-angle-left" />
                                    </PaginationLink>
                                </PaginationItem>

                                {renderPaginationItems(usersPage, usersTotal, usersLimit, (page) => fetchUsers(page))}

                                <PaginationItem disabled={usersPage >= usersTotalPages}>
                                    <PaginationLink
                                        href="#pablo"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            fetchUsers(usersPage + 1);
                                        }}
                                    >
                                        <i className="fas fa-angle-right" />
                                    </PaginationLink>
                                </PaginationItem>
                            </Pagination>
                        </nav>
                    </CardFooter>
                )}
            </Card>
        </>
    );
};

export default DashboardUsersTab;
